import cloneDeep from 'lodash/cloneDeep'
import { getSavedState, saveState } from '@state/helpers'

export const NETWORK_CONNECT_SCENARIO_STORAGE_KEY = 'mottocenter.network-connect.stub.scenario'

export const NETWORK_CONNECT_SCENARIO_DELAYS = Object.freeze({
  load: 600,
  save: 500,
  search: 400,
})

const RAPID = {
  key: 'rapid_responder',
  title: 'Rapid Responder',
  description:
    'Speed matters. This badge is earned by those who respond to referrals within the same day, demonstrating urgency, and commitment to excellent follow-up.',
}

const CONNECTOR = {
  key: 'network_connector',
  title: 'Network Connector',
  description:
    'Earned by users who send referrals to others that are then completed. Your outreach fuels collaboration, expands professional networks, and helps grow a stronger, more diverse book of business.',
}

function badges({ rapid, connector }) {
  return [
    { ...RAPID, earned: rapid },
    { ...CONNECTOR, earned: connector },
  ]
}

function stubOffice(overrides = {}) {
  return {
    id: 1,
    tradeName: 'Summit',
    city: 'Denver',
    state: 'CO',
    zipCode: '80202',
    ...overrides,
  }
}

function searchLo(id, name, networkBadges, officeOverrides = {}) {
  return {
    id,
    name,
    photoUrl: '',
    primaryOffice: stubOffice(officeOverrides),
    networkBadges,
  }
}

function baseUser(overrides = {}) {
  return {
    id: 4242,
    name: 'Jordan Banks',
    firstName: 'Jordan',
    lastName: 'Banks',
    jobTitle: 'Loan Originator',
    photoUrl: '',
    networkBio: 'Helping families find the right mortgage.',
    domain: 'jordanbanks.example.com',
    awards: [
      { id: 11, title: 'Top Producer', year: 2025 },
      { id: 12, title: 'Rising Star', year: 2025 },
    ],
    primaryOffice: stubOffice(),
    licenses: [{ state: 'CO', number: '123456' }],
    networkBadges: badges({ rapid: true, connector: true }),
    ...overrides,
  }
}

const scenarioDefinitions = [
  {
    id: 'my-info-all-earned',
    label: 'My Info — both badges earned',
    description: 'Full profile, awards, bio, and both badges earned.',
    build: () => baseUser(),
  },
  {
    id: 'my-info-none-earned',
    label: 'My Info — no badges earned',
    description: 'Unearned state: red Ban icons and unlock copy on both spots.',
    build: () => baseUser({ networkBadges: badges({ rapid: false, connector: false }) }),
  },
  {
    id: 'my-info-mixed',
    label: 'My Info — one badge earned',
    description: 'Rapid Responder earned, Network Connector not yet.',
    build: () => baseUser({ networkBadges: badges({ rapid: true, connector: false }) }),
  },
  {
    id: 'my-info-empty',
    label: 'My Info — sparse profile',
    description: 'No bio, no awards, no badges earned (edge layout).',
    build: () =>
      baseUser({
        networkBio: '',
        awards: [],
        networkBadges: badges({ rapid: false, connector: false }),
      }),
  },
  {
    id: 'lo-search-badge-combos',
    label: 'LO Search — every badge combo',
    description:
      'Recipient results covering both badges, each single badge, none earned, unknown keys, and long names.',
    view: 'lo-search',
    build: () => baseUser(),
    searchResults: () => [
      searchLo(9001, 'Alex Rivera', badges({ rapid: true, connector: true })),
      searchLo(9002, 'Sam Cohen', badges({ rapid: true, connector: false })),
      searchLo(9003, 'Riley Fox', badges({ rapid: false, connector: false })),
      searchLo(9004, 'Dana Whitfield', badges({ rapid: false, connector: true })),
      searchLo(9005, 'Morgan Ellis', []),
      searchLo(9006, 'Casey Drummond', [
        {
          key: 'mystery_award',
          title: 'Mystery Award',
          earned: true,
          description: 'Unknown badge key — exercises the Trophy fallback icon.',
        },
      ]),
      searchLo(
        9007,
        'Alexandria-Catherine Wolfeschlegelsteinhausenbergerdorff',
        badges({ rapid: true, connector: true }),
        {
          tradeName: 'Extraordinarily Long Tradename That Stresses The Layout',
        }
      ),
    ],
  },
]

const scenarioStateById = {}

function getScenarioDefinition(id) {
  return scenarioDefinitions.find((scenario) => scenario.id === id) || scenarioDefinitions[0]
}

function getOrCreateScenarioState(id) {
  const key = getScenarioDefinition(id).id
  if (!scenarioStateById[key]) {
    scenarioStateById[key] = getScenarioDefinition(key).build()
  }
  return scenarioStateById[key]
}

export const networkConnectScenarioOptions = scenarioDefinitions.map(({ id, label, description, view }) => ({
  id,
  label,
  description,
  view: view || 'my-info',
}))

export function getSelectedNetworkConnectScenarioId() {
  return getSavedState(NETWORK_CONNECT_SCENARIO_STORAGE_KEY) || scenarioDefinitions[0].id
}

export function setSelectedNetworkConnectScenario(id) {
  saveState(NETWORK_CONNECT_SCENARIO_STORAGE_KEY, getScenarioDefinition(id).id)
}

export function getNetworkConnectMyInfoResponse(id = getSelectedNetworkConnectScenarioId()) {
  return cloneDeep(getOrCreateScenarioState(id))
}

export function updateNetworkConnectBio(networkBio, id = getSelectedNetworkConnectScenarioId()) {
  const state = getOrCreateScenarioState(id)
  state.networkBio = networkBio
  return cloneDeep(state)
}

export function getNetworkConnectLoSearchResponse(id = getSelectedNetworkConnectScenarioId()) {
  const definition = getScenarioDefinition(id)
  if (definition.searchResults) {
    return {
      seedOrder: 1,
      currentPage: 1,
      lastPage: 1,
      data: definition.searchResults(),
    }
  }

  // A small default LO list covering earned-badge combinations.
  return {
    seedOrder: 1,
    currentPage: 1,
    lastPage: 1,
    data: [
      searchLo(9001, 'Alex Rivera', badges({ rapid: true, connector: true })),
      searchLo(9002, 'Sam Cohen', badges({ rapid: true, connector: false })),
      searchLo(9003, 'Riley Fox', badges({ rapid: false, connector: false })),
    ],
  }
}

export function waitForNetworkConnectDelay(type) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, NETWORK_CONNECT_SCENARIO_DELAYS[type] || 0)
  })
}
